import type { TableDataShape } from '@utilities/components/TableData/utilities/types';
import type { ProportionStatInput } from '@utilities/helpers/significance';

import {
  getColumnAccessor,
  OPTION_LABEL_ACCESSORS,
} from '@api/survey_dashboard/survey_responses/utilities/columns';

/**
 * Stable per-row identity, independent of display sort order or which table
 * (tableCount vs tablePct) is rendered. Prefer the immutable option id; fall
 * back to the option-label value. The render helper keys significance lookups
 * on this so client-side column sorting can't misattribute letters.
 *
 * Collision-safe for this table: the backend emits one comparison row per
 * DISTINCT `response_value` (see transform_response.py `.unique()`), so
 * `ans_opt` is unique per row even when `option_id` is absent. If that contract
 * ever changes, make `option_id` the guaranteed-unique key here.
 */
export const getSignificanceRowKey = (row: Record<string, unknown> | undefined): string =>
  String(row?.option_id ?? row?.ans_opt ?? row?.option_value ?? '');

export interface ProportionStatInputResult {
  input: ProportionStatInput;
  /** Segment column accessors, index-aligned with `input.groups`. */
  segmentAccessors: string[];
  /** Stable row key -> row index into the engine's per-row output. */
  statRowByKey: Record<string, number>;
}

/**
 * Build stat-test input from a report `tableCount` table.
 *
 * Returns `null` when the table cannot support a comparison — no table, or
 * fewer than two segment columns with a real numeric base (i.e. no Compare-By,
 * or a single segment). Low-base (small-`n`) segments are still included; the
 * engine suppresses their letters. Suppressed cells map to a `null` count
 * (excluded from comparisons) rather than a real 0. The option-label column is
 * excluded.
 */
export function buildProportionStatInput(
  tableCount: null | TableDataShape | undefined
): null | ProportionStatInputResult {
  const columns = tableCount?.columns;
  const rows = tableCount?.data;
  if (!columns?.length || !rows) return null;

  const segmentColumns = columns.filter((col) => {
    const accessor = getColumnAccessor(col);
    if (!accessor || OPTION_LABEL_ACCESSORS.includes(accessor)) return false;
    // A real segment column exposes a numeric respondent base in its footer.
    return toFiniteNumber(col.Footer) !== null;
  });
  if (segmentColumns.length < 2) return null;

  const segmentAccessors = segmentColumns.map((col) => getColumnAccessor(col) as string);
  const groups = segmentColumns.map((col) => {
    const accessor = getColumnAccessor(col) as string;
    return {
      n: toFiniteNumber(col.Footer) as number,
      options: rows.map((row) => ({ count: toFiniteNumber(row[accessor]) })),
    };
  });

  // Null prototype so an option literally named "__proto__" is an ordinary key.
  const statRowByKey: Record<string, number> = Object.create(null);
  for (let index = 0; index < rows.length; index++) {
    const key = getSignificanceRowKey(rows[index]);
    // Missing or duplicate row identity would misattribute letters — degrade
    // to no annotations rather than wrong ones.
    if (key === '' || key in statRowByKey) return null;
    statRowByKey[key] = index;
  }

  return { input: { groups }, segmentAccessors, statRowByKey };
}

function toFiniteNumber(value: unknown): null | number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  // A segment base may arrive display-formatted (e.g. `footer_format: '0,0'`
  // renders n=1234 as "1,234"). Accept plain decimals ("1234", "12.5") and
  // en-US grouped thousands ("1,234"); reject anything else ("12,5", "1,2,3",
  // "0x20") so a garbled footer drops the segment instead of misparsing.
  // Both patterns are fully anchored with disjoint quantifiers, so they
  // backtrack linearly — the safe-regex heuristic flags them as a false positive.
  // eslint-disable-next-line security/detect-unsafe-regex
  if (!/^\d+(\.\d+)?$/.test(trimmed) && !/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(trimmed)) return null;
  return Number(trimmed.replace(/,/g, ''));
}
